import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";
import { AuthContext } from "shared/context/auth-context";
import { useHttpClient } from "shared/hooks/http-hook";

const DemoDataBanner = ({ onLoadDemoData }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
  const SESSION_STORAGE_KEY = "demoBannerHiddenForSession";

  useEffect(() => {
    // Check if banner was temporarily hidden in this session
    const hiddenInSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (hiddenInSession === "true") {
      setIsTemporarilyHidden(true);
    }
  }, []);

  const handleCloseClick = () => {
    setShowCloseModal(true);
  };

  const handleJustClose = () => {
    setShowCloseModal(false);
    setIsTemporarilyHidden(true);
    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
  };

  const updateBannerStatus = async (show) => {
    setIsUpdating(true);
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/clients/${auth.id}/onboarding-status`,
        "PATCH",
        JSON.stringify({
          onboardingStatus: {
            showDemoBanner: show,
          },
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      // Reload client info to update the UI
      if (auth?.login) {
        await auth.login(auth.id, auth.token);
      }
    } catch (err) {
      console.error("Error updating banner status:", err);
      if (window?.toast?.error) {
        window.toast.error(t("anErrorOccurred"));
      }
    } finally {
      setIsUpdating(false);
      setShowCloseModal(false);
    }
  };

  const handleNeverShowAgain = async () => {
    await updateBannerStatus(false);
  };

  const handleCancelClose = () => {
    setShowCloseModal(false);
  };

  const handleLoadDemoData = async () => {
    if (onLoadDemoData) {
      onLoadDemoData();
      return;
    }

    setIsUpdating(true);
    try {
      await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/clients/${auth.id}/seed-sample-data`, "POST", null, {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      });

      if (window?.toast?.success) {
        window.toast.success(t("onboarding.demoDataSuccess"));
      }

      // Reload page to show new data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error("Error loading demo data:", err);
      if (window?.toast?.error) {
        window.toast.error(t("onboarding.demoDataError"));
      }
      setIsUpdating(false);
    }
  };

  // Check if banner should be shown based on client's onboarding status or temporary hide
  if (!auth?.client?.onboardingStatus?.showDemoBanner || isTemporarilyHidden) {
    return null;
  }

  return (
    <>
      <Modal
        show={showCloseModal}
        onCancel={handleCancelClose}
        header={t("onboarding.closeBannerModalTitle")}
        footer={
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <Button inverse onClick={handleJustClose} disabled={isUpdating}>
              {t("onboarding.justClose")}
            </Button>
            <Button danger onClick={handleNeverShowAgain} disabled={isUpdating}>
              {isUpdating ? t("onboarding.updating") : t("onboarding.neverShowAgain")}
            </Button>
          </div>
        }
      >
        <p>{t("onboarding.closeBannerModalText")}</p>
      </Modal>

      <div
        style={{
          backgroundColor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "6px",
          padding: "20px",
          marginBottom: "24px",
          position: "relative",
          transition: "box-shadow 0.2s ease",
        }}
      >
        {/* Close Button */}
        <button
          onClick={handleCloseClick}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "18px",
            color: "#6c757d",
            padding: "4px 8px",
            lineHeight: "1",
            borderRadius: "4px",
            transition: "color 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.color = "#495057";
          }}
          onMouseLeave={(e) => {
            e.target.style.color = "#6c757d";
          }}
          title={t("onboarding.closeBanner")}
        >
          ✕
        </button>

        {/* Content Area */}
        <div style={{ paddingRight: "32px" }}>
          <div
            style={{
              fontSize: "15px",
              fontWeight: "600",
              color: "#212529",
              marginBottom: "8px",
            }}
          >
            {t("onboarding.loadAllDemoData")}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#6c757d",
              marginBottom: "16px",
              lineHeight: "1.5",
            }}
          >
            {t("onboarding.loadAllDemoDataDescription")}
          </div>
          <button
            onClick={handleLoadDemoData}
            disabled={isUpdating}
            style={{
              backgroundColor: isUpdating ? "#6c757d" : "#0d6efd",
              color: "white",
              border: "none",
              borderRadius: "4px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: isUpdating ? "not-allowed" : "pointer",
              transition: "background-color 0.2s ease",
              opacity: isUpdating ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isUpdating) {
                e.target.style.backgroundColor = "#0b5ed7";
              }
            }}
            onMouseLeave={(e) => {
              if (!isUpdating) {
                e.target.style.backgroundColor = "#0d6efd";
              }
            }}
          >
            {isUpdating ? t("onboarding.loadingDemoData") : t("onboarding.loadDemoDataButton")}
          </button>
        </div>
      </div>
    </>
  );
};

export default DemoDataBanner;
